import { Box, IconButton, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { camelCase } from '@/utils/stringCaser';
import { format, useMask } from '@react-input/mask';
import { REGEX } from '@/constants/regex';
import { useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';

type SVSSNInputProps = {
  name: string;
  label: string;
  isTin?: boolean;
  disabled?: boolean;
  matchingInputName?: string;
};

export default function SVSSNInput({
  name,
  label,
  isTin = false,
  disabled = false,
  matchingInputName,
}: SVSSNInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [showSSN, setShowSSN] = useState(false);
  const [type, setType] = useState<React.HTMLInputTypeAttribute>('text');

  const maskOptions = {
    mask: '###-##-####',
    replacement: { '#': /\d/ },
    showMask: true,
  };
  const maskRef = useMask(maskOptions);
  const methods = useFormContext();

  const matchingInputValue = matchingInputName
    ? methods.watch(matchingInputName)
    : '';

  const errorLabel = isTin ? 'TIN' : 'SSN';
  const builtRulesObject = buildInputRulesObject(label, {
    required: true,
    pattern: {
      regex: REGEX.SSN,
      readablePatternName: errorLabel,
    },
    validate: (val: string) => {
      if (!matchingInputName) {
        return;
      }

      if (matchingInputValue && val !== matchingInputValue) {
        return `${errorLabel} and Confirm ${errorLabel} must be identical`;
      }
    },
  });

  return (
    <Controller
      control={methods.control}
      name={name}
      rules={builtRulesObject}
      render={({
        field: { value, onChange, onBlur },
        fieldState: { error },
      }) => (
        <Box sx={{ marginBottom: 2 }}>
          <TextField
            type={type}
            inputRef={maskRef}
            disabled={disabled}
            error={!!error?.message}
            helperText={error?.message}
            id={camelCase(label)}
            label={label}
            onChange={onChange}
            onFocus={() => {
              setType('text');
              setIsFocused(true);
            }}
            onBlur={() => {
              setShowSSN(false);
              setIsFocused(false);
              setType('password');
              onBlur();
            }}
            value={value ? format(value, maskOptions) : ''}
            sx={{ width: '100%' }}
            slotProps={{
              input: {
                endAdornment: isFocused ? null : (
                  <IconButton
                    onClick={() => {
                      setShowSSN(!showSSN);
                      setType(showSSN ? 'password' : 'text');
                    }}
                    sx={{ minWidth: 'fit-content' }}>
                    {showSSN ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              },
            }}
          />
        </Box>
      )}
    />
  );
}
